import { Vehicle } from '@/types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MapPin, Calendar, Gauge, Zap, Shield, Phone, Video, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface QuickSummaryProps {
  vehicle: Vehicle;
  onRequestVisit: () => void;
  onRequestVideo: () => void;
  onDownloadPDF: () => void;
}

export const QuickSummary = ({ 
  vehicle, 
  onRequestVisit, 
  onRequestVideo,
  onDownloadPDF 
}: QuickSummaryProps) => {
  const formatPrice = (price?: number) => {
    if (!price) return 'Prix sur demande';
    return `${price.toLocaleString('fr-FR')} €`;
  };

  const formatMileage = (mileage?: number) => {
    if (!mileage) return 'Non renseigné';
    return `${mileage.toLocaleString('fr-FR')} km`;
  };

  return (
    <Card className="p-6 sticky top-4 space-y-6">
      {/* Prix principal */}
      <div>
        <div className="text-3xl font-bold text-primary">
          {formatPrice(vehicle.priceTTC || vehicle.price)}
        </div>
        {vehicle.priceTTC && vehicle.priceHT && (
          <div className="text-sm text-muted-foreground">
            {formatPrice(vehicle.priceHT)} HT
          </div>
        )}
      </div>

      <Separator />

      {/* Informations clés */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 text-sm">
          <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
          <span className="font-medium">Val-de-Meuse, Haute-Marne (52)</span>
        </div>

        {vehicle.year && vehicle.year !== 'avant-1990' && (
          <div className="flex items-center gap-3 text-sm">
            <Calendar className="h-5 w-5 text-primary flex-shrink-0" />
            <span>{vehicle.year}</span>
          </div>
        )}

        {vehicle.mileage && (
          <div className="flex items-center gap-3 text-sm">
            <Gauge className="h-5 w-5 text-primary flex-shrink-0" />
            <span>{formatMileage(vehicle.mileage)}</span>
          </div>
        )}

        {vehicle.enginePower && (
          <div className="flex items-center gap-3 text-sm">
            <Zap className="h-5 w-5 text-primary flex-shrink-0" />
            <span>{vehicle.enginePower} ch</span>
          </div>
        )}

        {vehicle.euroStandard && (
          <div className="flex items-center gap-3 text-sm">
            <Shield className="h-5 w-5 text-primary flex-shrink-0" />
            <div className="flex items-center gap-2">
              <span>{vehicle.euroStandard}</span>
              {vehicle.critAir && (
                <Badge variant="outline" className="text-xs">
                  Crit'Air {vehicle.critAir === 'exempt' ? 'Exempté' : vehicle.critAir}
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Boutons d'action */}
      <div className="space-y-3">
        <Button 
          onClick={onRequestVisit} 
          className="w-full" 
          size="lg"
        >
          <Calendar className="mr-2 h-5 w-5" />
          Demander une visite
        </Button>

        <Button 
          onClick={onRequestVideo} 
          variant="outline" 
          className="w-full"
          size="lg"
        >
          <Video className="mr-2 h-5 w-5" />
          Demander une vidéo
        </Button>

        <Button 
          onClick={onDownloadPDF} 
          variant="outline" 
          className="w-full"
          size="lg"
        >
          <FileText className="mr-2 h-5 w-5" />
          Télécharger la fiche PDF
        </Button>

        <Button 
          asChild
          variant="default" 
          className="w-full bg-green-600 hover:bg-green-700"
          size="lg"
        >
          <a href="tel:+33608248529" className="flex items-center justify-center">
            <Phone className="mr-2 h-5 w-5" />
            06 08 24 85 29
          </a>
        </Button>
      </div>

      {/* Informations légales */}
      <div className="text-xs text-muted-foreground text-center pt-4 border-t">
        <p className="font-semibold">Trucks52 SARL</p>
        <p>2 Place de l'Église, Recourt</p>
        <p>52140 Val-de-Meuse</p>
        <p className="mt-2">SIRET: 508 009 495 00013</p>
      </div>
    </Card>
  );
};
